/* eslint-disable i18next/no-literal-string */
import {
  SetStateAction, useState,
} from 'react';

import { Passkey, VerifyRegistrationParams } from '@app/queryTyping';

import { logPasskeyDebug, logPasskeyError } from '@app/common/utils/logPasskey';

import { client } from '@app/core/apolloClient';

import {
  GetPasskeyOptionsDocument, GetPasskeyOptionsQuery, GetPasskeyOptionsQueryVariables,
} from '@app/widgets/contacts-and-settings/queries/queryTyping/getPasskeyOptions';
import { useGetRegisteredPasskeysLazyQuery } from '@app/widgets/contacts-and-settings/queries/queryTyping/getRegisteredPasskeys';
import { useVerifyRegistrationPassKeyMutation } from '@app/widgets/contacts-and-settings/queries/queryTyping/verifyRegistrationPassKey';

import {
  bufferEncode,
  convertCreateCredential,
  convertCreateOptions,
} from './webauthn-helpers';

const buildPublicKey = (passkeyOptions: GetPasskeyOptionsQuery) => convertCreateOptions(
  passkeyOptions.createPasskeyRegisterOptions,
);
type CustomAuthenticatorAttestationResponse = AuthenticatorAttestationResponse & {
  getAuthenticatorData?: () => ArrayBuffer;
  getPublicKey?: () => ArrayBuffer;
  getPublicKeyAlgorithm?: () => number;
  getTransports?: () => string[];
};

const getResponseValues = (publicKeyCredential?: PublicKeyCredential) => {
  const response = publicKeyCredential?.response as CustomAuthenticatorAttestationResponse;
  return {
    authenticatorData: response?.getAuthenticatorData?.() || null,
    pk: response?.getPublicKey?.() || null,
    pkAlgo: response?.getPublicKeyAlgorithm?.() || null,
    transports: response?.getTransports?.() || null,
  };
};

const buildVerificationVariables = (
  responseValues: any,
  publicKeyCredential?: PublicKeyCredential,
): { variables: { verifyRegistrationParams: VerifyRegistrationParams } } | null => (publicKeyCredential ? {
  variables: {
    verifyRegistrationParams: {
      nickName: '',
      ...convertCreateCredential(publicKeyCredential),
      response: {
        ...convertCreateCredential(publicKeyCredential).response,
        transports: responseValues.transports || [],
        authenticatorData: bufferEncode(responseValues.authenticatorData),
        publicKey: bufferEncode(responseValues.pk),
        publicKeyAlgorithm: responseValues.pkAlgo,
      },
    },
  },
} : null);

export const usePasskeyRegister = ({
  setErrorDialogVisibility,
  setSuccessDialogVisibility,
  isOnboarding,
}: {
  setErrorDialogVisibility: React.Dispatch<SetStateAction<boolean>>,
  setSuccessDialogVisibility: React.Dispatch<SetStateAction<boolean>>,
  isOnboarding?: boolean
}) => {
  const handleError = (err: Error) => {
    logPasskeyError(err, `Passkey register error: ${err.message}`);
    setErrorDialogVisibility(true);
  };

  const [registeredPasskeys, setRegisteredPasskeys] = useState<Passkey[]>([]);
  const [isPasskeyCreationLoading, setPasskeyCreationLoading] = useState(false);

  // Fetching the registered passkeys
  const [getRegisteredPasskeys, { loading: isPasskeysLoading }] = useGetRegisteredPasskeysLazyQuery({
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      setRegisteredPasskeys(data.getRegisteredPasskeys.passkeys);
    },
  });

  // Verifying the registration passkey
  const [verifyRegistration] = useVerifyRegistrationPassKeyMutation({
    onCompleted: (data) => {
      if (!data.verifyRegistration.success) {
        handleError(new Error(data.verifyRegistration.message));
      } else {
        setSuccessDialogVisibility(true);
        if (!isOnboarding) {
          getRegisteredPasskeys();
        }
      }
    },
  });

  const handleStartRegistration = async () => {
    setPasskeyCreationLoading(true);

    try {
      logPasskeyDebug('Passkey fetching options');

      const { data: passkeyOptions, error } = await client.query<
      GetPasskeyOptionsQuery, GetPasskeyOptionsQueryVariables>({
        query: GetPasskeyOptionsDocument,
        fetchPolicy: 'no-cache',
      });

      if (error) {
        throw error;
      }

      if (passkeyOptions) {
        logPasskeyDebug('Passkey options', passkeyOptions);

        const publicKey = buildPublicKey(passkeyOptions);
        logPasskeyDebug('publicKey', publicKey);

        if (!('credentials' in navigator)) {
          throw new Error('WebAuthn not supported in this browser.');
        }

        const publicKeyCredential = await navigator.credentials.create({
          publicKey,
        }) as PublicKeyCredential;

        logPasskeyDebug('publicKeyCredential', publicKeyCredential);

        const responseValues = getResponseValues(publicKeyCredential);
        logPasskeyDebug('responseValues', responseValues);

        const verificationVariables = buildVerificationVariables(responseValues, publicKeyCredential);
        logPasskeyDebug('Verification variables', verificationVariables);

        if (verificationVariables) {
          await verifyRegistration(verificationVariables);
        }
      }
    } catch (err: any) {
      handleError(err);
    } finally {
      setPasskeyCreationLoading(false);
    }
  };

  return {
    isLoading: isPasskeyCreationLoading || isPasskeysLoading,
    registeredPasskeys,
    getRegisteredPasskeys,
    handleStartRegistration,
  };
};
